/**
 * Auth Loading Gate
 *
 * CRITICAL: This component ensures the entire app waits for Firebase Auth
 * to resolve before rendering any routes or making any decisions.
 *
 * Without this gate, race conditions occur:
 * 1. App renders
 * 2. Guards check auth state (user is null because auth is still loading)
 * 3. Guards redirect to login
 * 4. Auth finishes loading (user is actually logged in)
 * 5. User sees login page briefly, then gets redirected back
 *
 * This gate prevents all of that by showing a loading screen until
 * auth state is definitively known.
 */

import { ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

interface AuthLoadingGateProps {
  children: ReactNode;
}

export function AuthLoadingGate({ children }: AuthLoadingGateProps) {
  const { isLoading } = useAuth();

  // Show full-page loading state until auth is resolved
  // This is the ONLY place where auth loading should block rendering
  if (isLoading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground animate-pulse">Loading...</p>
        </div>
      </div>
    );
  }

  // Auth is resolved - render children
  return <>{children}</>;
}
